import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, OnChanges, SimpleChanges ,OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProgressState } from '../../models/component-events.model';
import { PartDetail, StepProgress, QuoteDetails } from '../../quote-assist-v2.model';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { QuoteAssistV2Service, PdfDownloadParams, GetRfqQuoteIntelligenceRequest, SaveCardFeedbackRequest } from '../../../../services/quote-assist-v2.service';
import { ActivatedRoute } from '@angular/router';
import { QuoteAssistv2BaseComponent } from '../../main/quote-assist-v2-base.component';
import { ToastrService } from 'ngx-toastr';
import { MixPanelEvents } from '../../../shared/enums';
import { environment } from '../../../../environments/environment';

interface QuoteIntelligenceResponseItem {
  rfqPartId?: number;
  quoteIntelligenceData?: string;
  cardFeedback?: unknown;
}

type PartIntelligenceSnapshot = Pick<
  PartDetail,
  'risks' | 'riskCardData' | 'riskCardName' | 'riskCardDataRaw' | 'riskFeedbackAction'
>;
@Component({
  selector: 'app-progress-tracker',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmDialogComponent],
  templateUrl: './progress-tracker.component.html',
  styleUrls: ['./progress-tracker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProgressTrackerComponent extends QuoteAssistv2BaseComponent implements OnChanges,OnInit {
 
  @Input() progressState: ProgressState | null = null;
  @Input() parts: PartDetail[] = [];
  @Input() currentPartIndex: number = 0;
  @Input() validationErrors: { [key: string]: string } = {};
  @Input() rfqName: string = '';
  @Input() isDraftEnabled: boolean = false;
  @Input() isSubmitting: boolean = false;
  @Input() isConfirmQuote: boolean = false; // Track if quote has been confirmed
  @Input() isQuoteSubmitted: boolean = false; // Track if quote has been submitted
  @Input() integrationType: string = ''; // Added for conditional button visibility
  @Input() quoteDetails: QuoteDetails | null = null; // Added to access isQuoteDetailsCompleted
  @Input() contactId?: number; // Added for future functionality like PDF downloads
  @Input() isViewMode: boolean = false;
  @Output() stepClick = new EventEmitter<string>();
  @Output() partClick = new EventEmitter<number>();
  @Output() addNewPart = new EventEmitter<void>();
  @Output() deletePart = new EventEmitter<number>();
  @Output() addPartFiles = new EventEmitter<number>();
  @Output() rfqNameChange = new EventEmitter<string>();
  @Output() partFieldChange = new EventEmitter<{
    partIndex: number; 
    field: string; 
    value: any; 
    validationResult: { isValid: boolean; error?: string };
  }>();
  @Output() submitQuote = new EventEmitter<'submit' | 'draft'>();
  @Output() saveDraft = new EventEmitter<void>();
  @Output() stepNavigation = new EventEmitter<any>();
  @Output() internalNotesChange = new EventEmitter<{
    partIndex: number;
    internalNotes: string;
  }>();
  queryParams = {
    companyId: 0,
    rfqPartId: 0,
    contactId: 0,
    rfqStatusId: 0,
    isTrackingEnabled: 'false',
    integration: '',
    emailId:''
  };
  // Track field interaction state
  private rfqNameTouched: boolean = false;
  private rfqNameDirty: boolean = false;
  private rfqNameValidationTriggered: boolean = false; // NEW: Track if validation has been explicitly triggered
  
  // Track dropdown visibility state
  openDropdownIndex: number | null = null;
  dropdownPosition = { x: 0, y: 0 };
  
  // Track internal notes dropdown visibility state
  openInternalNotesIndex: number | null = null;
  internalNotesPosition = { x: 0, y: 0 };
  
  // Confirm dialog state
  showDeleteConfirmDialog: boolean = false;
  deleteDialogTitle: string = 'Delete Part';
  deleteDialogMessage: string = '';
  partToDelete: number | null = null;
  showPageDataLoading:boolean=false;
  /** RFQ id for which quote intelligence has been loaded in this component lifetime */
  private loadedQuoteIntelligenceRfqId: number | null = null;
  /** Prevent duplicate in-flight requests */
  private isQuoteIntelligenceLoading = false;
  /** Initialize UserGuiding only once per RFQ lifecycle */
  private hasInitializedUserGuiding = false;
  /** Set only from Proceed flow; initialize UG once intelligence data is present */
  private pendingUserGuidingInitialization = false;
  /** Snapshot of already-loaded intelligence so UI survives parts array replacement without API cache */
  private readonly partIntelligenceByPartId = new Map<number, PartIntelligenceSnapshot>();
  /** Index fallback when partId is missing/unstable */
  private readonly partIntelligenceByIndex = new Map<number, PartIntelligenceSnapshot>();
  /** Index of the part whose expandable section (e.g. Risks) is open; null when none expanded */
  expandedPartIndex: number | null = null;
  /** Part index for which quote intelligence (risks) API is loading */
  loadingPartIntelligenceIndex: number | null = null;
  constructor(
    private cdr: ChangeDetectorRef,
    private toastrService: ToastrService,
    private quoteAssistV2Service: QuoteAssistV2Service,
    private activatedRoute: ActivatedRoute) {
      super();
    }
  ngOnInit(): void {
    // TODO: Replace with actual API call
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['companyId']) {
        this.queryParams.companyId = Number(params['companyId']);
        this.queryParams.rfqPartId = Number(params['rfqPartId']);
        this.queryParams.contactId = Number(params['contactId']);
        this.queryParams.rfqStatusId = Number(params['rfqStatusId']);
        this.queryParams.isTrackingEnabled = params['isTrackingEnabled'];
        this.queryParams.integration = params['integration'];
        this.queryParams.emailId = params['emailId'] || '';
        this.isTrackingEnabled = this.queryParams.isTrackingEnabled ;
        this.ensureQuoteIntelligenceLoaded();
      }
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteDetails']) {
      const previousRfqId = this.parseRfqId(changes['quoteDetails'].previousValue as QuoteDetails | null | undefined);
      const currentRfqId = this.parseRfqId(this.quoteDetails);
      if (previousRfqId !== currentRfqId) {
        this.loadedQuoteIntelligenceRfqId = null;
        this.isQuoteIntelligenceLoading = false;
        this.hasInitializedUserGuiding = false;
        this.pendingUserGuidingInitialization = false;
        this.partIntelligenceByPartId.clear();
        this.partIntelligenceByIndex.clear();
      }
    }
    
    if (changes['parts'] && this.parts.length > 0) {
      const hasActivePart = this.parts.some(part => part.isActivePart === true);
          if (!hasActivePart && this.parts.length > 0) {
            // Set the first part as active by default
            this.parts[0].isActivePart = true;
          }
    }
    
    // Handle when a new part is added (parts array length increased)
    if (changes['parts'] && !changes['parts'].firstChange) {
      const previousParts = (changes['parts'].previousValue || []) as PartDetail[];
      this.snapshotIntelligence(previousParts);
      this.rehydrateIntelligenceIntoCurrentParts();

      const previousLength = changes['parts'].previousValue?.length || 0;
      const currentLength = this.parts.length;
      if (currentLength > previousLength) {
        if(this.integrationType === 'MFGDirect'){
          this.parts.forEach((part, index) => {
            part.isActivePart = index >= previousLength;
          });
          
          // The new part (at index previousLength) should be active
          if (this.parts[previousLength]) {
            this.parts[previousLength].isActivePart = true;
          }
        }
      }

    }

    // Keep intelligence panel aligned with externally selected part
    // (e.g. selecting a part from the costs "Quote" button flow).
    if (changes['currentPartIndex'] && this.parts.length > 0) {
      if (this.isQuoteSummaryStep()) {
        this.expandedPartIndex = null;
      } else {
        this.openPartIntelligenceForSelection(this.currentPartIndex);
      }
    }
    // When progress state changes and Parts step becomes active for the first time,
    // preload quote intelligence once for all parts.
    if (changes['progressState']) {
      const current = this.progressState;
      const previousStep = changes['progressState'].previousValue?.currentStep as string | undefined;
      const currentStep = current?.currentStep;
      const partsStepActive = current?.steps?.some(
        s => s.stepId === 'parts-information' && s.status === 'active'
      );
      if (currentStep === 'parts-information' && previousStep === 'details' && this.parts.length > 0) {
        this.openPartIntelligenceForSelection(this.currentPartIndex);
      }
      if (currentStep === 'costs') {
        this.expandedPartIndex = null;
      }
      if (partsStepActive) {
        this.ensureQuoteIntelligenceLoaded();
      }
    }

    if (changes['parts'] || changes['quoteDetails']) {
      this.ensureQuoteIntelligenceLoaded();
    }
  }

  onStepClick(stepId: string): void {
    if (this.isStepAccessible(stepId)) {
      this.stepClick.emit(stepId);
    }
  }

  onPartClick(partIndex: number): void {
    // console.log("onPartClick",partIndex);
    this.parts.forEach((part, index) => {
      part.isActivePart = index === partIndex;
    });
    this.openPartIntelligenceForSelection(partIndex);
    
    this.cdr.detectChanges();
    
    this.partClick.emit(partIndex);
  }

  private openPartIntelligenceForSelection(partIndex: number): void {
    if (partIndex < 0 || partIndex >= this.parts.length) {
      return;
    }

    this.expandedPartIndex = partIndex;
  }

  onToggleDropdown(partIndex: number, event: Event): void {
    event.stopPropagation();
    
    if(this.integrationType != 'MFGDirect'){
      return;
    }
    // If the same dropdown is clicked, close it; otherwise, open the new one
    if (this.openDropdownIndex === partIndex) {
      this.openDropdownIndex = null;
    } else {
      this.openDropdownIndex = partIndex;
      // Calculate dropdown position
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.dropdownPosition = {
        x: rect.right - 92, // Position dropdown to the right of the hamburger icon
        y: rect.bottom + 8   // Position below the hamburger icon
      };
    }
    
    this.cdr.detectChanges();
  }

  isDropdownOpen(partIndex: number): boolean {
    return this.openDropdownIndex === partIndex;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    this.openDropdownIndex = null;
    
    // Close internal notes dropdown when clicking outside
    this.openInternalNotesIndex = null;
    
    // Close internal notes popup when clicking outside
    this.closeInternalNotesOnOutsideClick(event);
  }

  onAddNewPart(): void {
    if (this.integrationType === 'MFG') {
      return;
    }
    
    if (this.parts.length >= 30) {
      return;
    }
    
    this.parts.forEach(part => {
      part.isActivePart = false;
    });
    
    this.cdr.detectChanges();    
   
    this.addNewPart.emit();
  }

  onSubmitQuote(): void {
    this.submitQuote.emit('submit');
  }

  onSaveDraft(): void {
    this.submitQuote.emit('draft');
  }

  /**
   * Delete a specific part with confirmation
   * Compatible with MFG integration - ensures array is properly updated
   * @param partIndex - Index of the part to delete
   * @param partName - Name of the part for confirmation message
   */
  onDeletePart(partIndex: number, partName?: string): void {
    // Prevent deletion if it's the only part
    if (this.parts.length <= 1) {
      return;
    }
    
    // Prevent deletion for MFG integration
    if (this.integrationType === 'MFG') {
      return;
    }

    // Get part name for confirmation
    const displayName = partName || this.parts[partIndex]?.partName || `Part ${partIndex + 1}`;
    
    // Close any open dropdowns
    this.openDropdownIndex = null;
    
    // Set up confirm dialog
    this.deleteDialogMessage = `Are you sure you want to delete "${displayName}"?\n\nThis action cannot be undone.`;
    this.partToDelete = partIndex;
    this.showDeleteConfirmDialog = true;
    

    
    // Force change detection for OnPush strategy to ensure immediate UI update
    this.cdr.detectChanges();
  }

  /**
   * Handle confirm dialog confirmation for part deletion
   */
  onConfirmDeletePart(): void {
    if (this.partToDelete !== null) {
      // Emit delete event to parent component
      this.deletePart.emit(this.partToDelete);
    }
    this.onCancelDeletePart();
  }

  /**
   * Handle confirm dialog cancellation
   */
  onCancelDeletePart(): void {

    this.showDeleteConfirmDialog = false;
    this.partToDelete = null;
    this.deleteDialogMessage = '';
    
    // Force change detection for OnPush strategy to ensure immediate UI update
    this.cdr.detectChanges();
  }

  onAddPartFiles(partIndex: number): void {  
    this.openDropdownIndex = null;
    this.addPartFiles.emit(partIndex);
  }

  /** Toggle expand/collapse of the part's extra section (e.g. Risks). */
  onTogglePartExpand(partIndex: number): void {
    const part = this.parts[partIndex];
    if (!part) {
      return;
    }
    if (this.expandedPartIndex === partIndex) {
      this.expandedPartIndex = null;
      this.cdr.detectChanges();
      return;
    }

    // Expanding a part
    this.expandedPartIndex = partIndex;
    this.parts.forEach((item, index) => {
      item.isActivePart = index === partIndex;
    });
    this.partClick.emit(partIndex);

    this.cdr.detectChanges();
  }

  /** Load quote intelligence once when Parts step first becomes active. */
  private loadQuoteIntelligenceOnce(): void {
    const rfqId = this.parseRfqId(this.quoteDetails);
    const companyId = this.queryParams.companyId;
    if (rfqId == null || !companyId) {
      return;
    }

    const payload: GetRfqQuoteIntelligenceRequest = {
      RfqId: Number(rfqId),
      RfqPartId: this.parts.map(p => p.partId).filter(id => id != null) as number[],
      CompanyId: companyId,
      ContactId: this.queryParams.contactId,
    };

    this.isQuoteIntelligenceLoading = true;
    this.loadingPartIntelligenceIndex = this.expandedPartIndex ?? 0;
    this.cdr.detectChanges();

    this.quoteAssistV2Service.getRfqQuoteIntelligence(payload).subscribe({
      next: (response) => {
        const responseData = (response as unknown as { data?: QuoteIntelligenceResponseItem[] })?.data;
        const intelligenceItems = Array.isArray(responseData) ? responseData : [];
        this.loadedQuoteIntelligenceRfqId = rfqId;
        this.isQuoteIntelligenceLoading = false;

        const intelligenceByPartId = new Map<number, QuoteIntelligenceResponseItem>();
        intelligenceItems.forEach((item) => {
          if (item?.rfqPartId != null) {
            intelligenceByPartId.set(item.rfqPartId, item);
          }
        });

        // When supplier_risk data is present for the first part, open the first record by default
        if (this.parts.length > 0 && this.expandedPartIndex === null && !this.isQuoteSummaryStep()) {
          const firstPartId = this.parts[0]?.partId;
          const firstPartCache =
            firstPartId != null
              ? intelligenceByPartId.get(firstPartId)
              : undefined;
          if (firstPartCache && this.responseItemHasSupplierRisks(firstPartCache)) {
            this.expandedPartIndex = 0;
          }
        }

        this.parts.forEach((part) => {
          const apiData = part?.partId != null ? intelligenceByPartId.get(part.partId) : undefined;
          this.applyRiskIntelligenceFromApi(part, apiData);
        });
        this.snapshotIntelligence(this.parts);
        this.tryInitializeUserGuidingIfReady();

        this.loadingPartIntelligenceIndex = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isQuoteIntelligenceLoading = false;
        this.loadingPartIntelligenceIndex = null;
        this.cdr.detectChanges();
        this.toastrService.error('Failed to load quote intelligence.');
      },
    });
  }

  private ensureQuoteIntelligenceLoaded(): void {
    const rfqId = this.parseRfqId(this.quoteDetails);
    if (rfqId == null || !this.queryParams.companyId || this.parts.length === 0) {
      return;
    }

    if (this.loadedQuoteIntelligenceRfqId === rfqId || this.isQuoteIntelligenceLoading) {
      return;
    }

    this.loadQuoteIntelligenceOnce();
  }

  private parseRfqId(quoteDetails: QuoteDetails | null | undefined): number | null {
    const rawRfqId = quoteDetails?.rfqId;
    if (rawRfqId == null || rawRfqId === '') {
      return null;
    }

    const parsedRfqId = Number(rawRfqId);
    return Number.isFinite(parsedRfqId) ? parsedRfqId : null;
  }
// Build Triggered User Guiding
  public triggerUserGuidingInitialization(): void {
    this.pendingUserGuidingInitialization = true;
    this.tryInitializeUserGuidingIfReady();
  }

  private tryInitializeUserGuidingIfReady(): void {
    if (!this.pendingUserGuidingInitialization) {
      return;
    }

    const hasIntelligenceData = this.parts.some((part) => Boolean(part?.riskCardData?.bullets?.length));
    if (!hasIntelligenceData) {
      return;
    }

    const initialized = this.initializeUserGuiding();
    if (initialized) {
      this.pendingUserGuidingInitialization = false;
    }
  }

  private initializeUserGuiding(): boolean {
    if (this.hasInitializedUserGuiding) {
      return true;
    }

    const contactId = this.queryParams.contactId;
    if (!contactId) {
      return false;
    }

    const identify = (window as any)?.userGuiding?.identify;
    if (typeof identify !== 'function') {
      return false;
    }
    let visitorId = String(contactId);

    if (environment.environmentName != "production") {
      visitorId = environment.environmentName + "_" + visitorId;
    }



    identify(visitorId, {
      contactId:+ String(contactId),
      role: 'Supplier',
      companyId:this.queryParams.companyId ,
      email:this.queryParams.emailId,

    });
    this.hasInitializedUserGuiding = true;
    return true;
  }

  /** Returns true if quoteIntelligenceData contains supplier_risks in supplier_cards */
  private responseItemHasSupplierRisks(apiData: QuoteIntelligenceResponseItem): boolean {
    if (!apiData?.quoteIntelligenceData) return false;
    try {
      const parsed = JSON.parse(apiData.quoteIntelligenceData || '[]') as Array<{ supplier_cards?: Array<{ id?: string }> }>;
      const supplierCards = parsed?.[0]?.supplier_cards || [];
      return supplierCards.some((c) => c?.id === 'supplier_risks');
    } catch {
      return false;
    }
  }

  /** Returns true if the part has parsed supplier_risk bullets attached */
  partHasSupplierRisks(partIndex: number): boolean {
    const part = this.parts[partIndex];
    return Boolean(part?.riskCardData?.bullets?.length);
  }

  /** Apply API intelligence (supplier_risks card from supplier_cards) directly to a part model. */
  private applyRiskIntelligenceFromApi(part: PartDetail | undefined, apiData?: QuoteIntelligenceResponseItem): void {
    if (!part) {
      return;
    }

    part.riskCardData = undefined;
    part.riskCardName = undefined;
    part.riskCardDataRaw = undefined;
    part.riskFeedbackAction = undefined;
    part.risks = [];

    if (!apiData || !apiData.quoteIntelligenceData) {
      return;
    }

    this.applyRiskFeedbackFromApi(part, apiData.cardFeedback);

    try {
      const parsedArray = JSON.parse(apiData.quoteIntelligenceData || '[]') as Array<{
        supplier_cards?: Array<Record<string, unknown>>;
      }>;

      const supplierCards = parsedArray?.[0]?.supplier_cards || [];
      const riskCard = supplierCards.find(card => card?.['id'] === 'supplier_risks');

      if (riskCard) {
        const bullets = this.normalizeRiskBullets(riskCard['bullets']);
        const title = typeof riskCard['title'] === 'string' && riskCard['title'].trim() !== ''
          ? riskCard['title']
          : 'Supplier Checks';
        const description = typeof riskCard['description'] === 'string'
          ? riskCard['description']
          : null;
        const cardName = typeof riskCard['id'] === 'string' && riskCard['id']
          ? riskCard['id']
          : 'supplier_risks';

        part.riskCardData = {
          title,
          description,
          bullets,
        };
        part.riskCardName = cardName;
        part.riskCardDataRaw = JSON.stringify(riskCard);
        part.risks = bullets.map((bullet) => bullet.text);
      }
    } catch {
      part.riskCardData = undefined;
      part.riskCardName = undefined;
      part.riskCardDataRaw = undefined;
      part.risks = [];
    }
  }

  private applyRiskFeedbackFromApi(part: PartDetail, rawFeedback: unknown): void {
    let feedbackAction: 'like' | 'dislike' | undefined;

    if (Array.isArray(rawFeedback)) {
      const matched = rawFeedback.find((feedbackItem) => {
        if (!this.isRecord(feedbackItem)) {
          return false;
        }
        return feedbackItem['cardName'] === 'supplier_risks';
      });

      if (this.isRecord(matched)) {
        const action = matched['action'];
        if (action === 'like' || action === 'dislike') {
          feedbackAction = action;
        }
      }
    } else if (this.isRecord(rawFeedback)) {
      const action = rawFeedback['action'];
      if (action === 'like' || action === 'dislike') {
        feedbackAction = action;
      }
    }

    part.riskFeedbackAction = feedbackAction;
  }

  private snapshotIntelligence(parts: PartDetail[]): void {
    parts.forEach((part, index) => {
      const snapshot = this.extractPartIntelligenceSnapshot(part);
      if (!this.hasPartIntelligenceSnapshot(snapshot)) {
        return;
      }

      this.partIntelligenceByIndex.set(index, snapshot);
      if (part?.partId != null) {
        this.partIntelligenceByPartId.set(part.partId, snapshot);
      }
    });
  }

  private rehydrateIntelligenceIntoCurrentParts(): void {
    this.parts.forEach((part, index) => {
      const snapshot = part?.partId != null
        ? this.partIntelligenceByPartId.get(part.partId) ?? this.partIntelligenceByIndex.get(index)
        : this.partIntelligenceByIndex.get(index);

      if (!part || !snapshot) {
        return;
      }

      if (!part.riskCardData && snapshot.riskCardData) {
        part.riskCardData = snapshot.riskCardData;
      }
      if (!part.riskCardName && snapshot.riskCardName) {
        part.riskCardName = snapshot.riskCardName;
      }
      if (!part.riskCardDataRaw && snapshot.riskCardDataRaw) {
        part.riskCardDataRaw = snapshot.riskCardDataRaw;
      }
      if ((!part.risks || part.risks.length === 0) && snapshot.risks) {
        part.risks = snapshot.risks;
      }
      if (!part.riskFeedbackAction && snapshot.riskFeedbackAction) {
        part.riskFeedbackAction = snapshot.riskFeedbackAction;
      }
    });
  }

  private extractPartIntelligenceSnapshot(part: PartDetail): PartIntelligenceSnapshot {
    return {
      risks: part?.risks,
      riskCardData: part?.riskCardData,
      riskCardName: part?.riskCardName,
      riskCardDataRaw: part?.riskCardDataRaw,
      riskFeedbackAction: part?.riskFeedbackAction,
    };
  }

  private hasPartIntelligenceSnapshot(snapshot: PartIntelligenceSnapshot): boolean {
    return Boolean(
      (snapshot.risks && snapshot.risks.length > 0) ||
      snapshot.riskCardData ||
      snapshot.riskCardName ||
      snapshot.riskCardDataRaw ||
      snapshot.riskFeedbackAction
    );
  }

  private normalizeRiskBullets(rawBullets: unknown): { text: string; header?: string | null }[] {
    if (!Array.isArray(rawBullets)) {
      return [];
    }

    const normalizedBullets: { text: string; header?: string | null }[] = [];

    rawBullets.forEach((bullet) => {
      if (!this.isRecord(bullet)) {
        return;
      }

      const text = typeof bullet['text'] === 'string' ? bullet['text'].trim() : '';
      if (!text) {
        return;
      }

      const header = bullet['header'];
      normalizedBullets.push({
        text,
        header: typeof header === 'string' ? header : null,
      });
    });

    return normalizedBullets;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  isPartExpanded(partIndex: number): boolean {
    return this.expandedPartIndex === partIndex;
  }

  private isQuoteSummaryStep(): boolean {
    return this.progressState?.currentStep === 'costs';
  }

  onRiskFeedback(partIndex: number, action: 'like' | 'dislike'): void {
    const part = this.parts[partIndex];
    if (!part) return;
    const previousAction = part.riskFeedbackAction;
    if (previousAction === action) {
      part.riskFeedbackAction = undefined;
    } else {
      part.riskFeedbackAction = action;
    }
    this.cdr.detectChanges();

    const rfqId = this.quoteDetails?.rfqId;
    const contactId = this.queryParams.contactId;
    if (rfqId == null || rfqId === '' || part.partId == null || contactId == null || !part.riskCardDataRaw) {
      return;
    }
    const payload: SaveCardFeedbackRequest = {
      rfqId: Number(rfqId),
      rfqPartId: part.partId,
      contactId: contactId,
      cardName: part.riskCardName ?? 'supplier_risks',
      cardData: part.riskCardDataRaw,
      action,
    };
    this.quoteAssistV2Service.saveCardFeedback(payload).subscribe({
      next: () => {},
      error: () => {
        if (previousAction != null) {
          part.riskFeedbackAction = previousAction;
        } else {
          part.riskFeedbackAction = undefined;
        }
        this.cdr.detectChanges();
        this.toastrService.error('Failed to save feedback.');
      },
    });
  }

  getRiskFeedbackAction(partIndex: number): 'like' | 'dislike' | undefined {
    const part = this.parts[partIndex];
    return part?.riskFeedbackAction;
  }

  onRfqNameChange(value: string): void {
    // Mark field as touched and dirty when user changes it
    this.rfqNameTouched = true;
    this.rfqNameDirty = true;
    this.rfqNameValidationTriggered = true; // Explicitly trigger validation
    
    // Validate RFQ name before emitting
    const validationResult = this.validateRfqName(value);
    this.rfqNameChange.emit(value);
  }

  onRfqNameFocus(): void {
    // Mark field as touched when user focuses on it
    this.rfqNameTouched = true;
    // Don't trigger validation on focus - only track that field was touched
  }

  onRfqNameBlur(): void {
    // Mark field as touched when user leaves the field
    this.rfqNameTouched = true;
    // Don't trigger validation on blur - only track that field was touched
  }

  validateRfqName(value: string): { isValid: boolean; error?: string } {
    const trimmedValue = value?.trim();
    
    // Only show required error if validation has been explicitly triggered and field has been touched
    if (!trimmedValue) {
      if (this.rfqNameValidationTriggered && this.rfqNameTouched) {
        return { isValid: false, error: 'RFQ Name is required' };
      }
      return { isValid: true };
    }

    // Only validate length if validation has been explicitly triggered
    if (this.rfqNameValidationTriggered) {
      if (trimmedValue.length < 2) {
        return { isValid: false, error: 'RFQ Name must be at least 2 characters' };
      }

      if (trimmedValue.length > 100) {
        return { isValid: false, error: 'RFQ Name must be no more than 100 characters' };
      }
    }

    return { isValid: true };
  }

  getRfqNameError(): string | undefined {
    // Check if RFQ name has validation error
    const validationResult = this.validateRfqName(this.rfqName);
    return validationResult.isValid ? undefined : validationResult.error;
  }

  getRfqNameParentClass(): string {
    const hasError = this.getRfqNameError();
    let classes = 'editable-textbox input-group enter-rfq-name';
    
    // Only show error styling if validation has been explicitly triggered
    if (hasError && this.rfqNameValidationTriggered) {
      classes += ' error-fields';
    }
    
    return classes;
  }

  /**
   * Manually trigger RFQ Name validation
   * This should be called when user attempts to proceed without entering RFQ name
   */
  triggerRfqNameValidation(): void {
    this.rfqNameValidationTriggered = true;
    this.rfqNameTouched = true;
    // Force change detection to update the UI immediately
    this.cdr.detectChanges();
  }

  onPartFieldChange(partIndex: number, field: string, value: any): void {
    // Prevent changes to partName and partNumber when integration type is 'MFG'
    if (this.integrationType === 'MFG' && (field === 'partName' || field === 'partNumber')) {
      return;
    }
    
    // Validate for duplicates before emitting
    const validationResult = this.validatePartField(partIndex, field, value);
    this.partFieldChange.emit({ partIndex, field, value, validationResult });
  }

  validatePartField(partIndex: number, field: string, value: any): { isValid: boolean; error?: string } {
    // Skip validation for partName and partNumber when integration type is 'MFG'
    if (this.integrationType === 'MFG' && (field === 'partName' || field === 'partNumber')) {
      return { isValid: true };
    }
    
    if (field === 'partName' || field === 'partNumber') {
      const trimmedValue = value?.trim();
      
      if (!trimmedValue) {
        return { isValid: false, error: `${field === 'partName' ? 'Part Name' : ''} ` };
      }

      // Check for duplicates (excluding current part)
      const isDuplicate = this.parts.some((part, index) => {
        if (index === partIndex) return false; // Skip current part
        
        if (field === 'partName') {
          return part.partName?.trim().toLowerCase() === trimmedValue.toLowerCase();
        } else {
          return part.partNumber?.trim().toLowerCase() === trimmedValue.toLowerCase();
        }
      });

      if (isDuplicate) {
        const fieldName = field === 'partName' ? 'Part Name' : 'Part Number';
        return { isValid: false, error: `${fieldName} already exists` };
      }

      // Additional validation rules
      if (field === 'partName') {
        if (trimmedValue.length < 2) {
          return { isValid: false, error: '' };
        }
        if (trimmedValue.length > 100) {
          return { isValid: false, error: '' };
        }
      } else if (field === 'partNumber') {
        if (trimmedValue.length < 2) {
          return { isValid: false, error: '' };
        }
        if (trimmedValue.length > 50) {
          return { isValid: false, error: '' };
        }
      }
    }

    return { isValid: true };
  }

  getFieldError(partIndex: number, field: string): string | undefined {
    // Return validation error for this field
    const errorKey = `part${partIndex}_${field}`;
    return this.validationErrors[errorKey];
  }

  isStepAccessible(stepId: string): boolean {
    const step = this.progressState?.steps.find(step => step.stepId === stepId);
    const baseAccessibility = step?.isAccessible || false;
    
    // If isConfirmQuote is true, the third step (costs) should be accessible
    // when navigating from step 1 or step 2
    if (this.isConfirmQuote && stepId === 'costs') {
      return true;
    }
    
    // If quote is not submitted and step is parts-information, return false
    if (!this.quoteDetails?.isQuoteDetailsCompleted && stepId === 'parts-information') {
      return false;
    }
    
    return baseAccessibility;
  }

  getStepStatus(stepId: string): string {
    return this.progressState?.steps.find(step => step.stepId === stepId)?.status || 'pending';
  }

  getStepCircleStatus(stepId: string): string {
    // If isQuoteSubmitted is true and this is the costs step (Confirm Quote), show as completed
    if (this.isQuoteSubmitted && stepId === 'costs') {
      return 'completed';
    }
    
    // If isConfirmQuote is true and this is the costs step, show as active
    if (this.isConfirmQuote && stepId === 'costs') {
      return 'active';
    }
    
    // If quote details are completed and this is the details step, show as completed
    if (this.quoteDetails?.isQuoteDetailsCompleted && stepId === 'details') {
      return 'completed';
    }
    
    return this.getStepStatus(stepId);
  }

  getStepIcon(stepId: string): string {
    const status = this.getStepStatus(stepId);
    
    // If isQuoteSubmitted is true and this is the costs step (Confirm Quote), show blue checkmark
    if (this.isQuoteSubmitted && stepId === 'costs') {
      return 'assets/img/svg/blue-check.svg'; // Blue checkmark when quote is submitted
    }
    
    // If isConfirmQuote is true and this is the costs step, show as active
    if (this.isConfirmQuote && stepId === 'costs') {
      return 'assets/img/svg/gray-check.svg'; // Active icon
    }
    
    switch (status) {
      case 'completed':
        return 'assets/img/svg/blue-check.svg';
      case 'active':
        return 'assets/img/svg/gray-check.svg';
      case 'error':
        return 'assets/img/svg/gray-check.svg'; // Using gray round icon instead of error icon
      default:
        return 'assets/img/svg/gray-check.svg';
    }
  }

  getStepClass(stepId: string): string {
    const status = this.getStepStatus(stepId);
    const isAccessible = this.isStepAccessible(stepId);
    
    let classes = 'step-item';
    
    // If isQuoteSubmitted is true and this is the costs step (Confirm Quote), make it completed
    if (this.isQuoteSubmitted && stepId === 'costs') {
      classes += ' completed';
    } else if (this.isConfirmQuote && stepId === 'costs') {
      classes += ' active';
    } else if (status === 'active') {
      classes += ' active';
    } else if (status === 'completed') {
      classes += ' completed';
    } else if (status === 'error') {
      classes += ' error';
    } else {
      classes += ' pending';
    }
    
    return classes;
  }

  getStepNumber(stepId: string): number {
    const stepIndex = this.progressState?.steps.findIndex(step => step.stepId === stepId) || 0;
    return stepIndex + 1;
  }

  hasValidationErrors(stepId: string): boolean {
    const step = this.progressState?.steps.find(s => s.stepId === stepId);
    return (step?.validationErrors?.length || 0) > 0;
  }

  getValidationErrorCount(stepId: string): number {
    const step = this.progressState?.steps.find(s => s.stepId === stepId);
    return step?.validationErrors?.length || 0;
  }

  // Part-specific methods
  getPartClass(partIndex: number): string {
    let classes = 'part-item';
    
    if (partIndex === this.currentPartIndex) {
      classes += ' active';
    }
    
    return classes;
  }

  /**
   * Build container classes for part row based on multiple conditions
   */
  getPartContainerClasses(part: PartDetail): { [klass: string]: boolean } {
    if (!part) {
      return {};
    }
    
    const isActive = !!part.isActivePart;
    const isCompleted = !!part.isPartDetailCompleted;
    const isError = !!part.isValidated && !part.isValid;

    return {
      'error-part': isError,
      // 'active-part': isActive && !isCompleted,
      'inactive-part': !isActive,
      'quoted-part': isActive && isCompleted,
      'inactive-quoted-part': !isActive && isCompleted,
    };
  }

  getPartTitle(part: PartDetail, index: number): string {
    return part.partName || `Part ${index + 1}`;
  }

  getPartNumber(part: PartDetail): string {
    return part.partNumber || 'No Part Number';
  }

  hasPartFiles(part: PartDetail): boolean {
    return part.files && part.files.length > 0;
  }

  /**
   * Check if all parts are completed
   */
  areAllPartsCompleted(): boolean {
    return this.parts.length > 0 && this.parts.every(part => part.isPartDetailCompleted);
  }

  /**
   * Check if the Submit Quote button should be enabled
   * Button should only be enabled when:
   * 1. At least one part has been added
   * 2. All parts in the part list have isPartDetailCompleted = true
   * 3. Not currently submitting
   */
  isSubmitQuoteEnabled(): boolean {
    const hasParts = this.parts.length > 0;
    const allPartsCompleted = this.parts.every(part => part.isPartDetailCompleted);
    
    const isEnabled = hasParts && allPartsCompleted 
       && !this.isViewMode && !this.isQuoteSubmitted;
        
    return isEnabled;
  }

  /**
   * Get tooltip text for the Submit Quote button based on current state
   */
  getSubmitQuoteButtonTooltip(): string {
    if (this.isSubmitting) {
      return 'Generating quote...';
    }
    
    if (this.parts.length === 0) {
      return 'Add at least one part to submit quote';
    }
    
    const incompleteParts = this.parts.filter(part => !part.isPartDetailCompleted);
    if (incompleteParts.length > 0) {
      const partNames = incompleteParts.map(part => part.partName || `Part ${this.parts.indexOf(part) + 1}`).join(', ');
      return `Complete all parts before submitting: ${partNames}`;
    }
    
    // Return different tooltip based on integration type
    if (this.integrationType === 'MFGDirect') {
      return 'Send via Email';
    }
    
    return 'Submit your quote';
  }

  /**
   * Get the class for the "Add new part" icon based on completion status
   */
  getAddNewPartIconClass(): string {
    return this.areAllPartsCompleted() ? 'add-icon completed' : 'add-icon';
  }

  /**
   * Check if the "Confirm Quote" step should show the gray rounded icon
   * Now returns false to use the standard icon system for consistency
   */
  shouldShowConfirmQuoteIcon(stepId: string): boolean {
    return false; // Use standard icon system for all steps
  }

  /**
   * Get the class for the "Confirm Quote" icon based on completion status
   */
  getConfirmQuoteIconClass(stepId: string): string {
    if (stepId === 'costs') {
      // If quote has been submitted, show as completed (blue)
      if (this.isQuoteSubmitted) {
        return 'completed-checkmark';
      }
      // Otherwise use the normal status logic
      const status = this.getStepStatus(stepId);
      return status === 'completed' ? 'completed-checkmark' : 'pending-checkmark';
    }
    return '';
  }

  /**
   * Toggle the internal notes flag for a specific part
   * @param partIndex - Index of the part to toggle internal notes for
   */
  onToggleInternalNotes(partIndex: number, event?: Event): void {
    if (partIndex >= 0 && partIndex < this.parts.length) {
      // If the same internal notes is clicked, close it; otherwise, open the new one
      if (this.openInternalNotesIndex === partIndex) {
        this.openInternalNotesIndex = null;
        this.parts[partIndex].isInternalNotesEnabled = false;
      } else {
        // Close any previously open internal notes
        this.parts.forEach(part => {
          part.isInternalNotesEnabled = false;
        });
        
        this.openInternalNotesIndex = partIndex;
        this.parts[partIndex].isInternalNotesEnabled = true;
        
        // Calculate position if event is provided
        if (event) {
          const target = event.target as HTMLElement;
          const rect = target.getBoundingClientRect();
          this.internalNotesPosition = {
            x: rect.left - 75, // Position to the left of the icon
            y: rect.bottom + 8  // Position below the icon
          };
        }
      }
      
      this.internalNotesChange.emit({
        partIndex: partIndex,
        internalNotes: this.parts[partIndex].internalNotes || ''
      });
      
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle internal notes change on blur
   * @param partIndex - Index of the part
   * @param internalNotes - The internal notes value
   */
  onInternalNotesBlur(partIndex: number, internalNotes: string): void {
    if (partIndex >= 0 && partIndex < this.parts.length) {
      // Update the internal notes
      this.parts[partIndex].internalNotes = internalNotes;
      
      // Emit the change
      this.internalNotesChange.emit({
        partIndex: partIndex,
        internalNotes: internalNotes
      });
      

    }
  }

  /**
   * Close internal notes popup when clicking outside
   * @param event - The click event
   */
  private closeInternalNotesOnOutsideClick(event: Event): void {
    // Check if any internal notes popup is open
    const hasOpenInternalNotes = this.parts.some(part => part.isInternalNotesEnabled);
    
    if (hasOpenInternalNotes) {
      // Check if the click target is outside the internal notes popup
      const target = event.target as HTMLElement;
      const internalNotesElement = target.closest('.buyer-notes');
      
      // If click is outside the internal notes popup, close all open ones
      if (!internalNotesElement) {
        this.parts.forEach(part => {
          if (part.isInternalNotesEnabled) {
            part.isInternalNotesEnabled = false;
          }
        });
        
        // Force change detection for OnPush strategy to ensure immediate UI update
        this.cdr.detectChanges();
        

      }
    }
  }

downloadPDFUsingAPI(part: PartDetail) {
  let files: any[] = [];
  part.files.forEach(file => {
    files.push(file.fileName.split('/').pop() || file.fileName);
  });
  const inputPuram: any = {
    files: files,
    partId: part.partId+"",
    source: this.integrationType,
    rfqId: this.quoteDetails?.rfqId,  
    companyId: this.queryParams.companyId,
    contactId: this.queryParams.contactId
  };
  this.showPageDataLoading = true;
  this.quoteAssistV2Service.downloadPartAllFiles(inputPuram).subscribe({
    next: (response) => {
      this.showPageDataLoading = false;
      if(response.result){
        // Validate URL before proceeding
        if (this.isValidUrl(response.data.fileName)) {
          this.createClickRemoveLink(response.data.fileName);   
          this.toastrService.success('Files will be downloaded in a few seconds', 'Success');
        } else {
          console.error('Invalid URL received from API:', response.data.fileName);
          this.toastrService.error('Could not download files at this moment', 'Error');
        }
      }else{
        this.toastrService.error(response.message, 'Error');
      }
      this.cdr.detectChanges();
    },
    error: (error) => {
      this.showPageDataLoading = false;
      this.cdr.detectChanges();
      console.error('PDF download error:', error);
      this.toastrService.error('Failed to download PDF', 'Error');
    }
  });
}

  /**
   * Validates if a string is a valid URL
   * @param urlString - The string to validate
   * @returns boolean - true if valid URL, false otherwise
   */
  private isValidUrl(urlString: string): boolean {
    if (!urlString || typeof urlString !== 'string' || urlString.trim() === '') {
      return false;
    }

    try {
      const url = new URL(urlString.trim());
      // Check if it's a valid HTTP/HTTPS URL
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  /**
   * Download all files for a specific part
   * @param part - The part containing files to download
   */
  downloadPartFiles(part: PartDetail) {
    
    if (!part) {
      return;
    }
    
    if (part.files && part.files.length > 0) {
      
      // Check if any files have valid URLs
      const filesWithUrls = part.files.filter(file => file.fileURL);
      const filesWithoutUrls = part.files.filter(file => !file.fileURL);
      
      if (filesWithoutUrls.length > 0) {
        const missingFiles = filesWithoutUrls.map(f => f.fileName).join(', ');
        this.toastrService.error(`Some files do not have valid URLs and cannot be downloaded: ${missingFiles}\n\nPlease try refreshing the page or contact support.`);
      }
      
      // If we have files with valid URLs, proceed with download
      if (filesWithUrls.length > 0) {

        
        if (filesWithUrls.length === 1) {
          this.createClickRemoveLink(filesWithUrls[0].fileURL,filesWithUrls[0].fileName);
        } else {    
          this.downloadPDFUsingAPI(part);
        }
        
      } else if (filesWithoutUrls.length > 0) {
        // All files have issues
        alert(`No files can be downloaded for part: ${part.partName}. All files are missing valid URLs.`);
      }
    } else {
      alert(`No files found for part: ${part.partName}`);
    }
  }

  // TrackBy functions for performance
  trackByStepId(index: number, step: StepProgress): string {
    return step.stepId;
  }

  trackByPartIndex(index: number, part: PartDetail): number {
    return index;
  }

  trackByError(index: number, error: string): string {
    return error;
  }

  // Demo method for testing different states
  getDemoProgressState(): ProgressState {
    return {
      currentStep: 'parts',
      steps: [
        {
          stepId: 'details',
          title: 'Add Quote Details',
          status: 'completed',
          isAccessible: true,
          validationErrors: []
        },
        {
          stepId: 'parts',
          title: 'Parts',
          status: 'active',
          isAccessible: true,
          validationErrors: []
        },
        {
          stepId: 'quote',
          title: 'Confirm Quote',
          status: 'pending',
          isAccessible: false,
          validationErrors: []
        }
      ],
      overallProgress: 33,
      canProceed: true,
      canGoBack: true
    };
  }

  /**
   * Handle PDF download for MFGDirect integration
   */
  
  getDownloadPdfDataV2() {
     
    if (!this.quoteDetails) {
      console.error('Quote details not available');
      return;
    }
 
    // Create const variable for API parameters
    const pdfParams: PdfDownloadParams = {
      version: 2,
      companyId: this.queryParams.companyId,
      rfqId: this.quoteDetails.rfqId,
      contactId: this.queryParams.contactId
    };

    this.showPageDataLoading = true;
    this.cdr.detectChanges();
    
    this.quoteAssistV2Service.getWebsiteAndPrivateRfqQuotePDF(pdfParams).subscribe({
      next: (response) => {
        this.showPageDataLoading = false;
        this.cdr.detectChanges();
        this.downloadPdfFileFromResponse(response,pdfParams);        
        this.toastrService.success('PDF downloaded successfully', 'Success');
        if(this.integrationType === 'MFGDirect'){
        this.addTracking(MixPanelEvents.DownloadPDfSelfCreated, this.queryParams);
        }else{
          this.addTracking(MixPanelEvents.DownloadPDfWebsiteSubmitted, this.queryParams);
        }
      },
      error: (error) => {
        this.showPageDataLoading = false;
        this.cdr.detectChanges();
        console.error('PDF download error:', error);
        this.toastrService.error('Failed to download PDF', 'Error');
      }
    });
 
}

} 